import store from '@/store'
import { allRoles, isAuthorized, lazyLoadView, piloteAndAuditor } from '@/router/routing'
import type { Route, RouteConfig } from 'vue-router'
import {
  CAMPAIGN_RESULTS_ROUTE,
  CAMPAIGNS_EVALUATIONS_ROUTE,
  CAMPAIGNS_ROOT_ROUTE,
  CAMPAIGNS_ROUTE,
} from '@/contexts/campaigns/Campaigns.constants'

import { FeatureFlag } from '@/contexts/shared/feature-flags/FeatureFlags.interfaces'
import { hasFeatureFlagAccess } from '@/contexts/shared/feature-flags/FeatureFlags.helpers'

export const campaignsRoutes: RouteConfig = {
  path: '/campaigns',
  name: CAMPAIGNS_ROOT_ROUTE,
  component: () => lazyLoadView(import('@/contexts/campaigns/CampaignsRedirectionView.vue')),
  // Only used by the menu entry: the "/campaigns" URL is matched by the child route below
  redirect: () => ({
    name: hasFeatureFlagAccess(store.getters.customer_features_flags, FeatureFlag.CAMPAIGN_EVAL_GROUP_BY)
      ? CAMPAIGNS_EVALUATIONS_ROUTE
      : CAMPAIGNS_ROUTE,
  }),
  meta: {
    i18nTitle: 'campaigns.evaluations.title',
    icon: 'check-square',
    is_authorized: (route: Route) => isAuthorized(route),
    requires_auth: true,
    requires_tenant: true,
    requires_role: piloteAndAuditor,
    doc_id: 'campaign',
    feature_id: 'CAMPAIGNS',
  },
  children: [
    {
      path: '/campaigns',
      name: CAMPAIGNS_ROUTE,
      component: () => lazyLoadView(import('@/contexts/campaigns/CampaignsListView.vue')),
      meta: {
        i18nTitle: 'campaigns.evaluations.title',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: true,
        requires_role: piloteAndAuditor,
        hide_in_breadcrumb: true,
        doc_id: 'campaign',
        feature_id: 'CAMPAIGNS',
        // CampaignsView is rendered on this route when the flag is disabled, see CampaignsRedirectionView
        hasNeutralContentBackground: () =>
          hasFeatureFlagAccess(store.getters.customer_features_flags, FeatureFlag.CAMPAIGN_EVAL_GROUP_BY),
      },
    },
    {
      path: 'results/:campaign_id/:perimeter_id?',
      name: CAMPAIGN_RESULTS_ROUTE,
      component: () => lazyLoadView(import('@/contexts/campaigns/CampaignDetailsView.vue')),
      meta: {
        title: null,
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: true,
        requires_role: piloteAndAuditor,
        doc_id: 'campaign_details',
      },
      children: [
        {
          path: 'eval-details',
          name: 'campaign_eval_details',
          component: () => lazyLoadView(import('@/contexts/campaigns/CampaignEvaluationView.vue')),
          meta: {
            title: null,
            is_authorized: (route: Route) => isAuthorized(route),
            requires_auth: true,
            requires_tenant: true,
            requires_role: allRoles,
            challenge_mode: true,
            doc_id: 'campaign_eval_details',
          },
        },
      ],
    },
    {
      path: '/evaluations',
      name: CAMPAIGNS_EVALUATIONS_ROUTE,
      component: () => lazyLoadView(import('@/contexts/campaigns/CampaignsEvaluationsListView.vue')),
      meta: {
        i18nTitle: 'campaigns.evaluations.title',
        is_authorized: (route: Route) => isAuthorized(route),
        requires_auth: true,
        requires_tenant: true,
        requires_role: piloteAndAuditor,
        requires_feature_flag: FeatureFlag.CAMPAIGN_EVAL_GROUP_BY,
        hide_in_breadcrumb: true,
        doc_id: 'campaign',
        feature_id: 'CAMPAIGNS',
        hasNeutralContentBackground: true,
      },
    },
  ],
}
